import React, { forwardRef, HTMLAttributes, useMemo } from 'react';
import DOMPurify from 'dompurify';

import { getColor, PaletteColorsCustomizable } from '@spazious/dollhouse-theme';
import { styled } from '@spazious/dollhouse-theme';

interface AnnotationHtmlLabelProps extends Omit<HTMLAttributes<HTMLDivElement>, 'onInput'> {
  editable: boolean;
  content: string;
  variant?: 'display' | 'headline' | 'body' | 'label' | 'title';
  size?: 'large' | 'medium' | 'small';
  color?: PaletteColorsCustomizable;
  ellipsis?: boolean;
  onFocus?: React.FocusEventHandler<HTMLDivElement>;
  onBlur?: React.FocusEventHandler<HTMLDivElement>;
  onInput?: (content: string) => void;
  outline: boolean;
  opacity?: number;
}

export const AnnotationHtmlLabel = styled(
  forwardRef<HTMLDivElement, AnnotationHtmlLabelProps>(
    ({ editable, content, onFocus, onBlur, onInput, outline, ...restProps }, ref) => {
      const sanitizedContent = useMemo(() => DOMPurify.sanitize(content), [content]);

      const handleInput = (event: React.FormEvent<HTMLDivElement>) => {
        const newContent = DOMPurify.sanitize(event.currentTarget.innerHTML);
        onInput?.(newContent);
      };
      return (
        <div
          dangerouslySetInnerHTML={{ __html: sanitizedContent }}
          ref={ref}
          contentEditable={editable}
          style={{ border: outline ? '1px solid orange' : 'none' }}
          onBlur={onBlur}
          onFocus={onFocus}
          onInput={handleInput}
          {...restProps}
        />
      );
    }
  )
)<AnnotationHtmlLabelProps>`
  min-width: 50px;
  min-height: 30px;
  background-color: var(--spz-sys-color-panel);
  border-radius: var(--shape-rounded-radius);
  padding: var(--spacing-padding);
  white-space: nowrap;
  ${({ variant = 'label ', size = 'small', color = 'inherit', ellipsis, opacity }) => `
    font-family: var(--spz-sys-typescale-${variant}-${size}-font-family-name);
    font-style: var(--spz-sys-typescale-${variant}-${size}-font-family-style);
    font-weight: var(--spz-sys-typescale-${variant}-${size}-font-weight);
    font-size: var(--spz-sys-typescale-${variant}-${size}-font-size);
    letter-spacing: var(--spz-sys-typescale-${variant}-${size}-letter-spacing);
    line-height: var(--spz-sys-typescale-${variant}-${size}-line-height);
    text-transform: var(--spz-sys-typescale-${variant}-${size}-text-transform);
    text-decoration: var(--spz-sys-typescale-${variant}-${size}-text-decoration);
    color: ${getColor(color)};
    opacity:${opacity};
    ${ellipsis ? `text-overflow: ellipsis; overflow: hidden;` : ''}
  `}
`;
